// Многоточие рядом с кнопкой «скопировать комментарии» в шапке панели комментариев.
// Два действия: «Скачать для монтажки» и «Залить в проект». Кладётся в
// timeliner_fr/src/components/CommentsBlock/ рядом с CommentsLayout.js.
//
//   <CommentsMoreMenu api={createNleApi({ versionId: version.id })} />
//
// `api` — адаптер с двумя методами (см. nleApi.js), оба возвращают fetch Response:
//   exportComments(format, { fps })  → файл для монтажки
//   injectProject(file, { fps })     → файл проекта с маркерами
//
// Стили — CommentsMoreMenu.css, в палитре панели (фон #181c24, меню #333b4b, NT Somic).

import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import "./CommentsMoreMenu.css";

const PROGRAMS = [
  { key: "fcpxml", name: "Final Cut Pro", ext: ".fcpxml", how: "File → Import → XML" },
  { key: "xmeml", name: "Premiere Pro", ext: ".xml", how: "File → Import" },
  { key: "edl", name: "DaVinci Resolve", ext: ".edl", how: "Timeline → Import → Timeline Markers from EDL" },
];
const FPS = ["23.976", "24", "25", "29.97", "30", "50", "59.94", "60"];

const pref = {
  get: (k, d) => { try { return localStorage.getItem(`nle.${k}`) ?? d; } catch { return d; } },
  set: (k, v) => { try { localStorage.setItem(`nle.${k}`, v); } catch { /* приватный режим */ } },
};

// ---- иконки: 16px, обводка 1.2, как остальные в панели -------------------------------

const DotsIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <circle cx="2" cy="8" r="1.5" fill="currentColor" /><circle cx="8" cy="8" r="1.5" fill="currentColor" /><circle cx="14" cy="8" r="1.5" fill="currentColor" />
  </svg>
);
const DownIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M8 2.5v8m0 0 3-3m-3 3-3-3M3 13.5h10" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const UpIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M8 10.5v-8m0 0 3 3m-3-3-3 3M3 13.5h10" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const CloseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="m4 4 8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
  </svg>
);

// ---- помощники ------------------------------------------------------------------------

function filenameFrom(resp, fallback) {
  const cd = resp.headers.get("Content-Disposition") || "";
  const m = /filename\*=UTF-8''([^;]+)/.exec(cd);
  return m ? decodeURIComponent(m[1]) : fallback;
}

function saveBlob(blob, name) {
  const url = URL.createObjectURL(blob);
  const a = Object.assign(document.createElement("a"), { href: url, download: name });
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

async function errorMessage(resp) {
  try {
    const d = await resp.json();
    return d.detail || d.message || resp.statusText;
  } catch {
    return resp.statusText || "ошибка сети";
  }
}

const plural = (n, one, few, many) => {
  const m10 = n % 10, m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
  return many;
};

// ---- частота кадров: одна тихая строка, раскрывается по клику ---------------------------

function FpsRow({ fps, onChange }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="nle-fps">
      <span className="nle-fps-label">частота кадров ролика</span>
      {open ? (
        <select className="nle-fps-select" value={fps} autoFocus
                onChange={(e) => { onChange(e.target.value); setOpen(false); }}
                onBlur={() => setOpen(false)}>
          {FPS.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
      ) : (
        <button type="button" className="nle-fps-value" onClick={() => setOpen(true)}>
          {fps} <span className="nle-fps-edit">изменить</span>
        </button>
      )}
    </div>
  );
}

// ---- модалка: тот же оверлей, что у AddCommentModal ------------------------------------

function Modal({ title, onClose, children, wide }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return ReactDOM.createPortal(
    <div className="nle-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`nle-modal${wide ? " nle-modal-wide" : ""}`} role="dialog" aria-modal="true" aria-label={title}>
        <div className="nle-modal-head">
          <span className="nle-modal-title">{title}</span>
          <button type="button" className="nle-icon-btn" onClick={onClose} aria-label="Закрыть"><CloseIcon /></button>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
}

// ---- диалог «Скачать для монтажки» ---------------------------------------------------

function DownloadDialog({ api, onClose }) {
  const [fps, setFps] = useState(() => pref.get("fps", "25"));
  const [last, setLast] = useState(() => pref.get("program", ""));
  const [busy, setBusy] = useState(null);      // key программы, для которой идёт запрос
  const [done, setDone] = useState(null);      // {program, filename}
  const [error, setError] = useState(null);

  const download = async (p) => {
    setBusy(p.key); setError(null); setDone(null);
    try {
      const resp = await api.exportComments(p.key, { fps });
      if (!resp.ok) throw new Error(await errorMessage(resp));
      const filename = filenameFrom(resp, `комментарии 7cut${p.ext}`);
      saveBlob(await resp.blob(), filename);
      pref.set("program", p.key); setLast(p.key);
      setDone({ program: p, filename });
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <Modal title="Скачать комментарии для монтажки" onClose={onClose}>
      <div className="nle-programs">
        {PROGRAMS.map((p) => (
          <button key={p.key} type="button"
                  className={`nle-program${last === p.key ? " nle-program-last" : ""}${busy === p.key ? " nle-program-busy" : ""}`}
                  disabled={Boolean(busy)} onClick={() => download(p)}>
            <span className="nle-program-name">{p.name}</span>
            <span className="nle-program-ext">{busy === p.key ? "готовим…" : p.ext}</span>
          </button>
        ))}
      </div>
      {done && (
        <p className="nle-note nle-note-ok" role="status">
          <span className="nle-sparkle">✦</span> Файл скачан. В {done.program.name}: {done.program.how} — и выберите его.
        </p>
      )}
      {error && <p className="nle-note nle-note-err" role="alert">Не получилось: {error}</p>}
      <FpsRow fps={fps} onChange={(v) => { setFps(v); pref.set("fps", v); }} />
    </Modal>
  );
}

// ---- диалог «Залить в проект» ------------------------------------------------------

function UploadDialog({ api, onClose }) {
  const [fps, setFps] = useState(() => pref.get("fps", "25"));
  const [drag, setDrag] = useState(false);
  const [state, setState] = useState({ step: "idle" });  // idle | busy | done | error
  const input = useRef(null);

  const handle = async (file) => {
    if (!file) return;
    setState({ step: "busy", name: file.name });
    try {
      const resp = await api.injectProject(file, { fps });
      if (!resp.ok) throw new Error(await errorMessage(resp));
      const blob = await resp.blob();
      const placed = Number(resp.headers.get("X-NLEX-Placed") || 0);
      const unplaced = Number(resp.headers.get("X-NLEX-Unplaced") || 0);
      const filename = filenameFrom(resp, file.name.replace(/(\.[^.]+)$/, " + 7cut$1"));
      setState({ step: "done", blob, filename, placed, unplaced });
    } catch (e) {
      setState({ step: "error", message: e.message });
    }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDrag(false);
    handle(e.dataTransfer.files?.[0]);
  };

  return (
    <Modal title="Залить комментарии в проект" onClose={onClose} wide>
      {state.step !== "done" && (
        <>
          <ul className="nle-how">
            <li><b>Final Cut Pro</b> — File → Export XML…</li>
            <li><b>Premiere Pro</b> — File → Export → Final Cut Pro XML…</li>
          </ul>
          <div className={`nle-drop${drag ? " nle-drop-active" : ""}${state.step === "busy" ? " nle-drop-busy" : ""}`}
               onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
               onDragLeave={() => setDrag(false)} onDrop={onDrop}
               onClick={() => state.step !== "busy" && input.current?.click()}
               role="button" tabIndex={0}
               onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && input.current?.click()}>
            {state.step === "busy" ? (
              <span className="nle-drop-text">Расставляем маркеры в «{state.name}»…</span>
            ) : (
              <>
                <span className="nle-drop-text">Перетащите файл проекта сюда</span>
                <span className="nle-drop-sub">или нажмите, чтобы выбрать · .fcpxml, .xml</span>
              </>
            )}
            <input ref={input} type="file" accept=".fcpxml,.xml" hidden
                   onChange={(e) => { handle(e.target.files?.[0]); e.target.value = ""; }} />
          </div>
          {state.step === "error" && (
            <p className="nle-note nle-note-err" role="alert">
              Не получилось: {state.message}. Нужен XML, выгруженный из Final Cut или Premiere.
            </p>
          )}
          <FpsRow fps={fps} onChange={(v) => { setFps(v); pref.set("fps", v); }} />
        </>
      )}

      {state.step === "done" && (
        <div className="nle-result" role="status">
          <p className="nle-result-title">
            <span className="nle-sparkle">✦</span> {state.placed} {plural(state.placed, "комментарий", "комментария", "комментариев")} на таймлайне
          </p>
          {state.unplaced > 0 && (
            <p className="nle-note">
              {state.unplaced} {plural(state.unplaced, "комментарий", "комментария", "комментариев")} за концом проекта — {plural(state.unplaced, "пропущен", "пропущены", "пропущены")}.
            </p>
          )}
          <button type="button" className="nle-primary" onClick={() => saveBlob(state.blob, state.filename)}>
            <DownIcon /> Скачать проект с комментариями
          </button>
          <p className="nle-note">Импортируйте файл обратно в программу — он откроется рядом с оригиналом, оригинал не тронется.</p>
          <button type="button" className="nle-link" onClick={() => setState({ step: "idle" })}>Залить другой файл</button>
        </div>
      )}
    </Modal>
  );
}

// ---- сама кнопка-многоточие с меню ---------------------------------------------------

export default function CommentsMoreMenu({ api, canUpload = true }) {
  const [open, setOpen] = useState(false);
  const [dialog, setDialog] = useState(null);   // "download" | "upload"
  const [pos, setPos] = useState({ top: 0, right: 0 });
  const btn = useRef(null);

  useLayoutEffect(() => {
    if (!open || !btn.current) return;
    const r = btn.current.getBoundingClientRect();
    setPos({ top: r.bottom + 8, right: Math.max(8, window.innerWidth - r.right) });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const close = (e) => {
      if (btn.current?.contains(e.target) || e.target.closest?.(".nle-menu")) return;
      setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", () => setOpen(false), { once: true });
    return () => { document.removeEventListener("mousedown", close); document.removeEventListener("keydown", onKey); };
  }, [open]);

  const pick = (d) => { setOpen(false); setDialog(d); };

  return (
    <>
      <button ref={btn} type="button" className={`nle-dots${open ? " nle-dots-open" : ""}`}
              aria-label="Ещё действия с комментариями" aria-haspopup="menu" aria-expanded={open}
              onClick={() => setOpen((v) => !v)}>
        <DotsIcon />
      </button>

      {open && ReactDOM.createPortal(
        <div className="nle-menu" role="menu" style={{ top: pos.top, right: pos.right }}>
          <button type="button" role="menuitem" className="nle-menu-item" onClick={() => pick("download")}>
            <DownIcon />
            <span>
              <span className="nle-menu-title">Скачать для монтажки</span>
              <span className="nle-menu-sub">Final Cut, Premiere, Resolve</span>
            </span>
          </button>
          {canUpload && (
            <button type="button" role="menuitem" className="nle-menu-item" onClick={() => pick("upload")}>
              <UpIcon />
              <span>
                <span className="nle-menu-title">Залить в проект</span>
                <span className="nle-menu-sub">маркеры прямо на вашем таймлайне</span>
              </span>
            </button>
          )}
        </div>,
        document.body,
      )}

      {dialog === "download" && <DownloadDialog api={api} onClose={() => setDialog(null)} />}
      {dialog === "upload" && <UploadDialog api={api} onClose={() => setDialog(null)} />}
    </>
  );
}
